import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import QrCode2Icon from "@mui/icons-material/QrCode2";
import { Grid, Box, Typography } from "@mui/material";
import { Search } from "lucide-react";
import axios from "axios";
import HeaderForm from "../../components/Header";
import TwoSectionCard from "../../components/Card";
import QRModal from "../../components/QRModal";
import Loader from "../../components/Loader";
import Button from "../../components/Button";
import LocationMapModal from "../../components/LocationMapModal";
import { setFilters } from "../../store/slices/filterSlice";
import { fetchHomeData, fetchReportStats, resetYards } from "../../store/slices/homeSlice";
import { resetReportYards } from "../../store/slices/reportSlice"; 
import { formatDateToISOString } from "../../utils/utils";
import { getErrorMessage } from "../../utils/error";
import { errorMessage } from "../../components/Toaster";
import { getLatestContainerLocation } from "./api";
import { VISO_SUPER_ADMIN } from "../../constants";

const allStages = [
  "At Entry",
  "Post De-stuffing",
  "After Repair",
  "During Stuffing",
  "Post Stuffing",
  "At Exit",
];

const Home = () => {
  const dispatch = useDispatch();
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [containerSearch, setContainerSearch] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [locationData, setLocationData] = useState(null);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationAddress, setLocationAddress] = useState("-");

  const role = localStorage.getItem("role_name");
  const isVisoAdmin = role === VISO_SUPER_ADMIN;
  const localCompanyId = localStorage.getItem("company_id");

  const filters = useSelector((state) => state.filters);
  const companyId = isVisoAdmin
    ? filters.selectedCompanyId || localCompanyId
    : localCompanyId;

  const { yardOptions, reportStats, isLoading, lastFetchedFilters } =
    useSelector((state) => state.home);
  const locationMapRef = useRef(new Map());

  const ASSET_LABELS = {
    Container: {
      qrDescription: "Customers scan to view container reports — no login needed",
      locationTitle: "Container Location",
      emptyError: "Enter a container number",
      locationDescription: "Search latest recorded location of any container",
    },
    IsoTank: {
      qrDescription: "Customers scan to view tank reports — no login needed",
      locationTitle: "Tank Location",
      emptyError: "Enter a Tank number",
      locationDescription: "Search latest recorded location of any tank",
    },
    Other: {
      qrDescription: "Customers scan to view asset reports — no login needed",
      locationTitle: "Asset Location",
      emptyError: "Enter a Asset number",
      locationDescription: "Search latest recorded location of any asset",
    },
  };
  const assetType = localStorage.getItem("asset_type") || "Container";
  const labels = ASSET_LABELS[assetType] || ASSET_LABELS["Container"];

  useEffect(() => {
    if (!companyId) return;
    if (yardOptions.length > 0) return;

    dispatch(fetchHomeData({ companyId, filters }))
      .unwrap()
      .then(({ allYardIds, defaultDates }) => {
        dispatch(
          setFilters({
            yards: filters.yards?.length > 0 ? filters.yards : allYardIds,
            startDate: filters.startDate || defaultDates.startDate,
            endDate: filters.endDate || defaultDates.endDate,
            filterMode: filters.filterMode || "date",
          })
        );
      })
      .catch((err) => {
        errorMessage(err || "Something went wrong.");
      });
  }, [companyId]);

  useEffect(() => {
    if (!filters.yards?.length || !filters.startDate || !filters.endDate) return;
    if (yardOptions.length === 0) return;

    const currentFilters = {
      yards: filters.yards,
      startDate: formatDateToISOString(filters.startDate, true),
      endDate: formatDateToISOString(filters.endDate, false),
    };

    const lastFilters = lastFetchedFilters
      ? {
          yards: lastFetchedFilters.yards,
          startDate: lastFetchedFilters.startDate,
          endDate: lastFetchedFilters.endDate,
        }
      : null;

    const filtersChanged =
      JSON.stringify(lastFilters) !== JSON.stringify(currentFilters);

    if (!filtersChanged) return;

    dispatch(
      fetchReportStats({
        yardIds: currentFilters.yards,
        startDate: currentFilters.startDate,
        endDate: currentFilters.endDate,
      })
    )
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching report stats.");
      });
  }, [filters.yards, filters.startDate, filters.endDate, yardOptions]);

  const reportMap = new Map(reportStats.map((stat) => [stat.stage_name, stat]));
  const completeReportStats = allStages.map((stage) => {
    const stat = reportMap.get(stage);
    return {
      stage_name: stage,
      total_reports: stat?.total_reports || 0,
      passed: stat?.passed || 0,
    };
  });

  const handleSubmit = (data) => {
    dispatch(
      setFilters({
        ...filters,
        yards: data.yards,
        startDate: data.startDate,
        endDate: data.endDate,
      })
    );
    dispatch(
      fetchReportStats({
        yardIds: data.yards,
        startDate: formatDateToISOString(data.startDate, true),
        endDate: formatDateToISOString(data.endDate, false),
      })
    )
      .unwrap()
      .catch((err) => {
        errorMessage(getErrorMessage(err) || "Error fetching report stats.");
      });
  };


  const reverseGeoSearch = async (lat, lon) => {
    try {
      const key = `${lat},${lon}`;
      if (locationMapRef.current.has(key))
        return locationMapRef.current.get(key);
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&zoom=18&addressdetails=0`
      );
      const data = response.data;
      locationMapRef.current.set(key, data);
      return data;
    } catch (error) {
      console.error("Error in reverseGeoSearch:", error);
      return null;
    }
  };

  const handleContainerSearch = async () => {
    const trimmed = containerSearch.trim().toUpperCase();
    if (!trimmed) { setSearchError(labels.emptyError); return; }
    if (!/^[A-Z]{4}[0-9]{7}$/.test(trimmed)) {
      setSearchError("Format: 4 letters + 7 digits (e.g. MSCU1234567)");
      return;
    }
    setSearchLoading(true);
    setSearchError("");
    setLocationData(null);
    setLocationAddress("-");
    try {
      const res = await getLatestContainerLocation({ containerNumber: trimmed, companyId });
      if (res?.data) {
        const data = res.data;
        let address = data.address || "";
        if (!address && data.latitude && data.longitude) {
          const geoRes = await reverseGeoSearch(data.latitude, data.longitude);
          address = geoRes?.display_name || "-";
        }
        setLocationAddress(address);
        setLocationData({ ...data, address });
        setLocationModalOpen(true);
        setContainerSearch("");
      } else {
        setSearchError("No location found for this container.");
      }
    } catch (err) {
      setSearchError(err?.response?.data?.message || "No location found for this container.");
    } finally {
      setSearchLoading(false);
    }
  };

  return (
    <>
      {isLoading ? (
        <Loader color="primary" size={40} />
      ) : (
        <Box sx={{ p: 4, display: "flex", flexDirection: "column", width: "100%", minHeight: "100vh", overflowY: "auto", background: "#f5f7fc", boxSizing: "border-box" }}>

          <HeaderForm
            yardOptions={yardOptions}
            onSubmit={handleSubmit}
          />

          {/* ── Stats Grid ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {completeReportStats.map((stat, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <TwoSectionCard
                  leftTitle={`Surveyed ${stat.stage_name}`}
                  leftValue={stat.total_reports.toString()}
                  rightTitle="Passed"
                  rightValue={stat.passed.toString()}
                  colorIndex={index}
                />
              </Grid>
            ))}
          </Grid>

          {/* ── QR + Container Location ── */}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ background: "#fff", borderRadius: "16px", border: "1px solid #e8ecf4", p: "18px 24px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, height: "100%" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <QrCode2Icon sx={{ color: "#2563eb", fontSize: 22 }} />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b" }}>Yard QR code</Typography>
                    <Typography sx={{ fontSize: 12, color: "#64748b" }}>{labels.qrDescription}</Typography>
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  label="Generate QR"
                  onClick={() => setQrModalOpen(true)}
                  startIcon={<QrCode2Icon />}
                  sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, background: "linear-gradient(135deg, #3182ce, #2b6cb0)", whiteSpace: "nowrap", flexShrink: 0 }}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ background: "#fff", borderRadius: "16px", border: "1px solid #e8ecf4", p: "18px 24px", display: "flex", alignItems: "flex-start", gap: 2, height: "100%" }}>
                <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "#f0fdf4", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Search size={20} color="#16a34a" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b", mb: 0.5 }}>{labels.locationTitle}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1 }}>{labels.locationDescription}</Typography>
                  <Box sx={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
                    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "4px" }}>
                      <input
                        type="text"
                        value={containerSearch}
                        placeholder="e.g. MSCU1234567"
                        onChange={(e) => {
                          setSearchError("");
                          const val = e.target.value.toUpperCase();
                          if (val.length > 11) return;
                          if (val.length <= 4) {
                            if (/^[A-Z]*$/.test(val)) setContainerSearch(val);
                          } else {
                            const letters = val.slice(0, 4);
                            const nums = val.slice(4);
                            if (/^[A-Z]{4}$/.test(letters) && /^[0-9]*$/.test(nums)) setContainerSearch(val);
                          }
                        }}
                        onKeyDown={(e) => e.key === "Enter" && handleContainerSearch()}
                        style={{ width: "100%", border: `1.5px solid ${searchError ? "#ef4444" : "#e2e8f0"}`, borderRadius: "8px", padding: "9px 14px", fontSize: "14px", fontWeight: 600, letterSpacing: "2px", color: "#1e293b", outline: "none", fontFamily: "'Courier New', monospace", boxSizing: "border-box", background: "#f8fafc", transition: "border-color 0.2s" }}
                        onFocus={(e) => { if (!searchError) e.target.style.borderColor = "#16a34a"; }}
                        onBlur={(e) => { if (!searchError) e.target.style.borderColor = "#e2e8f0"; }}
                      />
                      {searchError && <span style={{ fontSize: "11px", color: "#ef4444", fontWeight: 500 }}>⚠ {searchError}</span>}
                    </Box>
                    <button
                      onClick={handleContainerSearch}
                      disabled={searchLoading}
                      style={{ height: "40px", padding: "0 16px", background: searchLoading ? "#86efac" : "#16a34a", color: "#fff", border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 600, cursor: searchLoading ? "not-allowed" : "pointer", display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap", flexShrink: 0, transition: "background 0.2s", boxShadow: searchLoading ? "none" : "0 2px 8px rgba(22,163,74,0.25)" }}
                      onMouseEnter={(e) => { if (!searchLoading) e.currentTarget.style.background = "#15803d"; }}
                      onMouseLeave={(e) => { if (!searchLoading) e.currentTarget.style.background = "#16a34a"; }}
                    >
                      {searchLoading ? (
                        <span style={{ width: 13, height: 13, border: "2px solid #fff", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                      ) : (
                        <Search size={13} />
                      )}
                      {searchLoading ? "..." : "Search"}
                    </button>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {locationData && (
            <LocationMapModal
              open={locationModalOpen}
              onClose={() => setLocationModalOpen(false)}
              locationTitle={labels.locationTitle}
              latitude={locationData.latitude}
              longitude={locationData.longitude}
              address={locationAddress}
              block={locationData.block}
              bay={locationData.bay}
              row={locationData.row}
              tier={locationData.tier}
              accuracy={locationData.accuracy_meters}
            />
          )}
          {qrModalOpen && (
            <QRModal
              yardOptions={yardOptions}
              companyId={companyId}
              onClose={() => setQrModalOpen(false)}
            />
          )}
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </Box>
      )}
    </>
  );
};

export default Home;